import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  FileText,
  Download,
  Loader2,
  Truck,
  Store,
  MapPin,
  ShoppingCart,
  AlertTriangle,
  Calendar,
  Navigation,
  Clock,
  TrendingUp,
  Receipt,
  FileSpreadsheet,
  ChevronDown,
  LayoutDashboard,
  CheckCircle2,
  XCircle,
  Activity,
  Award,
  Bike,
  Package,
  Users,
  ClipboardList,
  ShieldCheck,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import * as XLSX from 'xlsx';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
} from 'recharts';

// ---------- Chart palette (kept consistent across the page) ----------
const CHART_COLORS = {
  primary: '#0284c7',     // sky-600
  success: '#10b981',     // emerald-500
  warning: '#f59e0b',     // amber-500
  danger: '#ef4444',      // red-500
  violet: '#8b5cf6',      // violet-500
  pink: '#ec4899',        // pink-500
  indigo: '#6366f1',      // indigo-500
  slate: '#64748b',       // slate-500
};

const PIE_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.success,
  CHART_COLORS.warning,
  CHART_COLORS.violet,
  CHART_COLORS.pink,
  CHART_COLORS.indigo,
  CHART_COLORS.danger,
  CHART_COLORS.slate,
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
// The card this sheet follows took effect in 2022, so there is nothing sensible
// to report before then.
const YEAR_CHOICES = Array.from(
  { length: new Date().getFullYear() - 2021 },
  (_, i) => new Date().getFullYear() - i,
);

// Records nest - a geo location, a contact list, the rate a claim was priced at.
// Flattened one level into dotted keys so every value gets its own column, with
// anything deeper rendered as JSON rather than silently dropped.
const flattenRecord = (record, prefix = '') => {
  const flat = {};
  Object.entries(record || {}).forEach(([key, value]) => {
    const name = prefix ? `${prefix}.${key}` : key;
    if (value === null || value === undefined) {
      flat[name] = '';
    } else if (Array.isArray(value)) {
      flat[name] = value.length ? JSON.stringify(value) : '';
    } else if (typeof value === 'object') {
      Object.assign(flat, flattenRecord(value, name));
    } else if (typeof value === 'boolean') {
      flat[name] = value ? 'Yes' : 'No';
    } else {
      flat[name] = value;
    }
  });
  return flat;
};

// Column order follows the first record, then anything new a later record brings,
// so a field only some records carry still gets a column.
const allFieldColumns = (rows) => {
  const seen = [];
  rows.forEach((row) => Object.keys(row).forEach((k) => {
    if (!seen.includes(k)) seen.push(k);
  }));
  return seen;
};

const formatINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

// The date the user is actually having, not the UTC one. toISOString() would
// hand back yesterday for anyone opening the page before 05:30 IST, so an early
// shift would land on the wrong day's report.
const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const shortDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
};

const Reports = () => {
  const { api, isAdmin, isSuperadmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [orderType, setOrderType] = useState('all');
  // Opens on today. The page is mostly used to check the day that is happening,
  // and a wider window is one tap on the From Date away.
  const [fromDate, setFromDate] = useState(todayLocal);
  const [toDate, setToDate] = useState(todayLocal);
  // The TADA Sheet is a monthly document, so it filters by month rather than by
  // an arbitrary range, and over several people at once.
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [sheetUsers, setSheetUsers] = useState([]);   // empty = everyone
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  // Visit Log looks at one kind at a time; the summary still counts them all.
  const [visitKind, setVisitKind] = useState('all');
  // The curated tables show what a report is FOR. The super admin sometimes needs
  // what a record actually IS - every field, mandatory or not, including the ones
  // no report was designed around.
  const [showAllFields, setShowAllFields] = useState(false);
  // The customer report can look at the whole master, not only what was added in
  // the window - "who has nobody called on" is a different question from "who is new".
  const [customerScope, setCustomerScope] = useState('all');
  const [uncoveredOnly, setUncoveredOnly] = useState(false);
  const [outletScope, setOutletScope] = useState('all');
  const [outletUncoveredOnly, setOutletUncoveredOnly] = useState(false);
  // 'all' | 'customer' | 'outlet' - a first visit brings one or the other onto the books.
  const [newVisitCreated, setNewVisitCreated] = useState('all');
  const [otherPurpose, setOtherPurpose] = useState('all');
  // Activity report: which processes, and whose. Empty means everything/everyone.
  const [activityKinds, setActivityKinds] = useState([]);
  const [activityUsers, setActivityUsers] = useState([]);
  const [activityDetail, setActivityDetail] = useState(null);
  const userPickerRef = useRef(null);

  // Close the picker when the click lands anywhere else. Without this it stays
  // open over the report and has to be dismissed with a second click on itself.
  useEffect(() => {
    if (!userPickerOpen) return undefined;
    const onPointerDown = (event) => {
      if (userPickerRef.current && !userPickerRef.current.contains(event.target)) {
        setUserPickerOpen(false);
      }
    };
    const onEscape = (event) => { if (event.key === 'Escape') setUserPickerOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [userPickerOpen]);

  const visibleUsers = useMemo(() => {
    const needle = userSearch.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => (u.name || '').toLowerCase().includes(needle)
      || (u.employee_code || '').toLowerCase().includes(needle)
      || (u.designation || '').toLowerCase().includes(needle));
  }, [users, userSearch]);
  const [reportData, setReportData] = useState(null);
  // Which report the payload in reportData belongs to. Switching tabs re-renders
  // before the new fetch lands, so without this a tab briefly receives the
  // previous tab's payload and reads fields that are not there.
  const [reportType, setReportType] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users/all');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [api]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, fetchUsers]);

  const fetchReport = useCallback(async (type) => {
    setLoading(true);
    setReportData(null);
    setReportType(null);
    try {
      let url;
      if (type === 'tada-sheet') {
        url = `/reports/tada-monthly?year=${year}&month=${month}`;
        if (sheetUsers.length) url += `&user_ids=${sheetUsers.join(',')}`;
      } else {
        url = `/reports/${type}?from_date=${fromDate}&to_date=${toDate}`;
        if (selectedUser !== 'all') {
          url += `&user_id=${selectedUser}`;
        }
        if (type === 'visit-log' && visitKind !== 'all') {
          url += `&kind=${encodeURIComponent(visitKind)}`;
        }
        if (type === 'customers') {
          url += `&scope=${customerScope}`;
        }
        if (type === 'outlet-master') {
          url += `&scope=${outletScope}`;
        }
        if (type === 'new-visits' && newVisitCreated !== 'all') {
          url += `&created=${newVisitCreated}`;
        }
        if (type === 'other-visits' && otherPurpose !== 'all') {
          url += `&purpose=${encodeURIComponent(otherPurpose)}`;
        }
        if (type === 'activity') {
          if (activityKinds.length) url += `&activities=${activityKinds.map(encodeURIComponent).join(',')}`;
          if (activityUsers.length) url += `&user_ids=${activityUsers.join(',')}`;
        }
      }
      if (type === 'orders' && orderType !== 'all') {
        url += `&order_type=${orderType}`;
      }
      const response = await api.get(url);
      setReportData(response.data);
      setReportType(type);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [api, fromDate, toDate, selectedUser, orderType, year, month, sheetUsers, visitKind, customerScope, outletScope, newVisitCreated, otherPurpose, activityKinds, activityUsers]);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchReport(activeTab);
    }
  }, [activeTab, fromDate, toDate, selectedUser, orderType, year, month, sheetUsers, visitKind, customerScope, outletScope, newVisitCreated, otherPurpose, activityKinds, activityUsers, fetchReport]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // ---------- Excel exporters ----------
  const exportToExcel = (data, filename, sheetName = 'Report') => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${filename}_${fromDate}_to_${toDate}.xlsx`);
    toast.success('Report exported successfully!');
  };

  const exportDistributors = () => {
    if (!reportData?.data) return;
    const exportData = reportData.data.map(d => ({
      'Code': d.code,
      'Name': d.name,
      'Route': d.route_name,
      'Zone': d.zone_name,
      'Address': d.address || '',
      'Pincode': d.pincode || '',
      'Contact Name': d.primary_contact?.name || '',
      'Contact Phone': d.primary_contact?.phone || '',
      'Latitude': d.geo_location?.lat || '',
      'Longitude': d.geo_location?.lng || '',
      'Created By': d.created_by_name || '',
      'Created At': formatDateTime(d.created_at),
    }));
    exportToExcel(exportData, 'Distributors_Report', 'Distributors');
  };

  const exportOutlets = () => {
    if (!reportData?.data) return;
    const exportData = reportData.data.map(o => ({
      'Outlet ID': o.outlet_id,
      'Name': o.outlet_name,
      'Type': o.outlet_type_name,
      'Route': o.route_name,
      'Zone': o.zone_name,
      'Distributor': o.distributor_name,
      'City': o.city_name,
      'Address': o.address || '',
      'Pincode': o.pincode || '',
      'Contact Name': o.primary_contact?.name || '',
      'Contact Phone': o.primary_contact?.phone || '',
      'Latitude': o.geo_location?.lat || '',
      'Longitude': o.geo_location?.lng || '',
      'Added By': o.added_by_name,
      'Created At': formatDateTime(o.created_at),
    }));
    exportToExcel(exportData, 'Outlets_Report', 'Outlets');
  };

  const exportVisits = () => {
    if (!reportData?.daily_summary) return;
    const visitsData = [];
    reportData.daily_summary.forEach(day => {
      day.visits.forEach((v, idx) => {
        visitsData.push({
          'Date': day.date,
          'User': day.user_name,
          'Visit #': idx + 1,
          'Time': formatDateTime(v.created_at),
          'Distributor': v.distributor_name || 'N/A',
          'Outlet': v.outlet_name || 'N/A',
          'Contact Person': v.contact_person || '',
          'Contact No.': v.contact_no || v.phone_number || '',
          'Purpose': v.purpose || '',
          'First Visit': v.is_first_visit ? 'Yes' : 'No',
          'Latitude': v.geo_location?.lat || '',
          'Longitude': v.geo_location?.lng || '',
          'Distance from Prev (KM)': v.distance_from_prev_km || 0,
          'Status': v.status,
        });
      });
    });
    const summaryData = reportData.daily_summary.map(d => ({
      'Date': d.date,
      'User': d.user_name,
      'Total Visits': d.total_visits,
      'Total KM Travelled': d.total_km,
    }));
    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(visitsData);
    const ws2 = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws1, 'Visits Detail');
    XLSX.utils.book_append_sheet(wb, ws2, 'Daily KM Summary');
    XLSX.writeFile(wb, `Visits_Report_${fromDate}_to_${toDate}.xlsx`);
    toast.success('Visit report exported with KM calculations!');
  };

  const exportOrders = () => {
    if (!reportData?.data) return;
    const exportData = [];
    reportData.data.forEach(o => {
      o.items?.forEach(item => {
        exportData.push({
          'Order Number': o.order_number,
          'Date': formatDateTime(o.created_at),
          'Order Type': o.order_type || 'N/A',
          'Route': o.route_name,
          'Distributor': o.distributor_name,
          'Outlet': o.outlet_name,
          'Product': item.product_name,
          'Rate': item.rate,
          'Quantity': item.quantity,
          'Line Total': item.line_total,
          'Order Total': o.total_value,
          'Created By': o.created_by_name,
          'Status': o.status,
        });
      });
    });
    exportToExcel(exportData, 'Orders_Report', 'Orders');
  };

  const exportDamages = () => {
    if (!reportData?.data) return;
    const exportData = [];
    reportData.data.forEach(d => {
      d.entries?.forEach(entry => {
        exportData.push({
          'Damage Number': d.damage_number,
          'Complaint Date': d.complaint_date,
          'Created At': formatDateTime(d.created_at),
          'Distributor': d.distributor_name,
          'Outlet': d.outlet_name,
          'Delivery Date': entry.delivery_date,
          'Delivery Slot': entry.delivery_slot,
          'UBD Date': entry.ubd_date,
          'SKU': entry.sku_name,
          'Quantity': entry.quantity,
          'Batch Number': entry.batch_number,
          'Nature of Complaint': entry.nature_name,
          'Created By': d.created_by_name,
          'Status': d.status,
        });
      });
    });
    exportToExcel(exportData, 'Damages_Report', 'Damages');
  };

  const exportExpenses = () => {
    if (!reportData?.data) return;
    const exportData = reportData.data.map(e => ({
      'Expense Number': e.expense_number,
      'Date': e.date,
      'Type': e.expense_type,
      'Travel Type': e.travel_type || '',
      'From': e.from_location || '',
      'To': e.to_location || '',
      'Mode of Travel': e.mode_of_travel || '',
      'Distance (KM)': e.distance_km || 0,
      'First Reporting': e.first_reporting_time || '',
      'Last Reporting': e.last_reporting_time || '',
      'Expense Category': e.expense_category || '',
      'Auto Calculated': e.is_auto_calculated ? 'Yes' : 'No',
      'Amount (₹)': e.amount,
      'Status': e.status,
      'Created By': e.created_by_name,
      'Created At': formatDateTime(e.created_at),
      'Approved By': e.approved_by || '',
      'Approval Remarks': e.approval_remarks || '',
    }));
    exportToExcel(exportData, 'Expenses_Report', 'Expenses');
  };

  // The statement routes need the auth header, so it is fetched as a blob and
  // handed to the browser rather than linked to directly.
  const downloadStatement = async (userId, name, ext) => {
    try {
      const res = await api.get(
        `/reports/tada-statement.${ext}?year=${year}&month=${month}&user_id=${userId}`,
        { responseType: 'blob' },
      );
      const href = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = href;
      link.download = `TADA_${(name || 'statement').replace(/\s+/g, '_')}_${year}_${String(month).padStart(2, '0')}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch {
      toast.error('Could not build the statement');
    }
  };

  const exportActivity = () => {
    if (!reportData?.data?.length) return;
    const rows = reportData.data.map((r) => ({
      'Activity Date': r.activity_date_ist,
      'Activity Time': r.activity_time_ist,
      'Activity': r.activity,
      'Activity By': r.activity_by,
      'Distributor': r.distributor,
      'Outlet': r.outlet,
      'GMAP URL': r.gmap_url,
      'Latitude': r.geo_location?.lat ?? '',
      'Longitude': r.geo_location?.lng ?? '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Activity');
    if (reportData.by_activity?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        reportData.by_activity.map((x) => ({ 'Activity': x.name, 'Count': x.total }))), 'By Activity');
    }
    if (reportData.by_user?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        reportData.by_user.map((x) => ({ 'User': x.name, 'Activities': x.total }))), 'By User');
    }
    XLSX.writeFile(wb, `Activity_${fromDate}_to_${toDate}.xlsx`);
    toast.success('Activity report exported');
  };

  const exportOtherVisits = () => {
    if (!reportData?.data?.length) return;
    const rows = reportData.data.map((r) => ({
      'Date': r.date,
      'Time': r.time,
      'User': r.user_name,
      'Designation': r.designation,
      'Purpose': r.purpose,
      'Contact Person': r.contact_person || '',
      'Contact No': r.contact_no || '',
      'Nearest Place': r.nearest_place,
      'Distance To Nearest (km)': r.nearest_place_km ?? '',
      'Latitude': r.geo_location?.lat ?? '',
      'Longitude': r.geo_location?.lng ?? '',
      'Status': r.status,
      'Manual Entry': r.is_manual_entry ? 'Yes' : 'No',
      'Approved Date': r.approved_date || '',
      'Approved Time': r.approved_time || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Other Visits');
    if (reportData.by_purpose?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(reportData.by_purpose.map((x) => ({
        'Purpose': x.name, 'Visits': x.total, 'Users': x.users,
      }))), 'By Purpose');
    }
    if (reportData.by_user?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(reportData.by_user.map((x) => ({
        'User': x.name, 'Visits': x.total, 'Distinct Purposes': x.purposes,
      }))), 'By User');
    }
    XLSX.writeFile(wb, `Other_Visits_${fromDate}_to_${toDate}.xlsx`);
    toast.success('Other visit report exported');
  };

  const exportNewVisits = () => {
    if (!reportData?.data?.length) return;
    const rows = reportData.data.map((r) => ({
      'Date': r.date,
      'Time': r.time,
      'User': r.user_name,
      'Designation': r.designation,
      'Created': r.created_kind === 'customer' ? 'Customer/Distributor'
                 : r.created_kind === 'outlet' ? 'Outlet' : 'Nothing linked',
      'Code': r.created_code,
      'Name': r.created_name,
      'Type': r.created_type,
      'Parent Customer': r.parent_customer,
      'City': r.city,
      'Route': r.route,
      'Zone': r.zone,
      'Declared Volume': r.declared_volume,
      'Purpose': r.purpose,
      'Contact Person': r.contact_person,
      'Contact No': r.contact_no,
      'Record Approval': r.approval_status,
      'Record Active': r.record_active ? 'Yes' : 'No',
      'Visit Status': r.status,
      'Manual Entry': r.is_manual_entry ? 'Yes' : 'No',
      'Latitude': r.geo_location?.lat ?? '',
      'Longitude': r.geo_location?.lng ?? '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'New Visits');
    [['by_user', 'User', 'By User'], ['by_zone', 'Zone', 'By Zone'],
     ['by_purpose', 'Purpose', 'By Purpose']].forEach(([key, label, sheet]) => {
      const src = reportData[key] || [];
      if (!src.length) return;
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(src.map((x) => ({
        [label]: x.name, 'New Visits': x.total, 'Customers': x.customers,
        'Outlets': x.outlets, 'Declared Volume': x.volume, 'Rejected': x.rejected,
      }))), sheet);
    });
    XLSX.writeFile(wb, `New_Visits_${fromDate}_to_${toDate}.xlsx`);
    toast.success('New visit report exported');
  };

  const exportOutletMaster = () => {
    if (!reportData?.data?.length) return;
    const segmentOrder = reportData.segment_order
      || ['Milk', 'Curd', 'Set Curd', 'Lassi', 'Paneer', 'Ghee'];
    const rows = reportData.data.map((o) => ({
      'Outlet ID': o.outlet_id,
      'Name': o.outlet_name,
      'Type': o.retailer_type_name,
      'Activation': o.activation_type || '',
      'Purpose': o.purpose || '',
      'Customer/Distributor': o.distributor_name,
      'Distributor Code': o.distributor_code,
      'Route': o.route_name,
      'Zone': o.zone_name,
      'City': o.city_name,
      'Address': o.address || '',
      'Pincode': o.pincode || '',
      'Asset': o.asset_name,
      'Contact Name': o.primary_contact_name,
      'Contact Phone': o.primary_contact_phone,
      'Segments': o.segments_count,
      'Total Volume': o.segment_volume_total,
      // Every segment, in the order the outlet form asks for them. Built from a
      // fixed list rather than each row's own keys, or a row with no Lassi would
      // push that column to the end of the sheet.
      ...Object.fromEntries(segmentOrder.map((seg) => [
        `Vol ${seg}`, (o.segment_volume_breakdown || {})[seg] ?? 0,
      ])),
      'Visits In Period': o.visits_in_period,
      'Last Visit': o.last_contact_date,
      'Last Visit Time': o.last_contact_time,
      'Last Visit Source': o.last_contact_source,
      'Visited By': o.last_contact_by,
      'Latitude': o.geo_location?.lat ?? '',
      'Longitude': o.geo_location?.lng ?? '',
      'Added By': o.added_by_display,
      'Added Date': o.added_date,
      'Added Time': o.added_time,
      'Approval': o.approval_status || 'approved',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Outlets');
    [['by_type', 'Type', 'By Type'], ['by_zone', 'Zone', 'By Zone'],
     ['by_activation', 'Activation', 'By Activation'], ['by_adder', 'Added By', 'By Added By']]
      .forEach(([key, label, sheet]) => {
        const src = reportData[key] || [];
        if (!src.length) return;
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(src.map((x) => ({
          [label]: x.name, 'Outlets': x.outlets, 'Visits': x.visits,
          'Never Visited': x.uncovered, 'Volume': x.volume,
        }))), sheet);
      });
    if (reportData.by_segment?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        reportData.by_segment.map((x) => ({
          'Segment': x.segment, 'Volume': x.volume, 'UOM': x.uom, 'Outlets': x.outlets,
        }))), 'By Segment');
    }
    XLSX.writeFile(wb, `Outlets_${fromDate}_to_${toDate}.xlsx`);
    toast.success('Outlet report exported');
  };

  const exportCustomers = () => {
    if (!reportData?.data?.length) return;
    const rows = reportData.data.map((c) => ({
      'Code': c.code,
      'Name': c.name,
      'Type': c.distributor_type || '',
      'Route': c.route_name,
      'Zone': c.zone_name,
      'City': c.city_name,
      'City Inferred': c.city_is_inferred ? 'Yes' : 'No',
      'Address': c.address || '',
      'Pincode': c.pincode || '',
      'Contact Name': c.primary_contact_name,
      'Contact Phone': c.primary_contact_phone,
      'Contacts On File': c.contacts_count,
      'Outlets': c.outlets_total,
      'Outlets Active': c.outlets_active,
      'Visits In Period': c.visits_in_period,
      'Last Visit': c.last_visit_at ? c.last_visit_at.slice(0, 10) : '',
      'Visited By': (c.visited_by || []).join(', '),
      'Has Location': c.has_location ? 'Yes' : 'No',
      'Latitude': c.geo_location?.lat ?? '',
      'Longitude': c.geo_location?.lng ?? '',
      'Added By': c.added_by_display,
      'Added Date': c.added_date,
      'Added Time': c.added_time,
      'Approval': c.approval_display,
      'Approval Was Required': c.approval_was_required ? 'Yes' : 'No',
      'Active': c.is_active ? 'Yes' : 'No',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Customers');
    ['by_type', 'by_zone', 'by_adder'].forEach((key, i) => {
      const src = reportData[key] || [];
      if (!src.length) return;
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(src.map((x) => ({
        [['Type', 'Zone', 'Added By'][i]]: x.name,
        'Customers': x.customers, 'Outlets': x.outlets,
        'Visits': x.visits, 'Never Visited': x.uncovered,
      }))), ['By Type', 'By Zone', 'By Added By'][i]);
    });
    XLSX.writeFile(wb, `Customers_${fromDate}_to_${toDate}.xlsx`);
    toast.success('Customer report exported');
  };

  const exportPunchRemovals = () => {
    if (!reportData?.data?.length) return;
    const rows = reportData.data.map((r) => ({
      'Attendance Date': r.date,
      'User': r.user_name,
      'Action': r.action,
      'Old Punch In': r.old_punch_in_time,
      'Old Punch Out': r.old_punch_out_time + (r.old_punch_out_auto ? ' (auto)' : ''),
      'New Punch Out': r.new_punch_out_time,
      'New Punch Out Location': r.new_punch_out_source === 'last_visit' ? 'Last visit'
        : r.new_punch_out_source === 'punch_in' ? 'Punch in point' : '',
      'TADA Claim Withdrawn': r.removed_tada_claim || '',
      'Reason': r.reason || '',
      'Done By': r.removed_by_name,
      'Done On': r.removed_on,
      'Done At': r.removed_time,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Removed Punches');
    XLSX.writeFile(wb, `Removed_Punches_${fromDate}_to_${toDate}.xlsx`);
    toast.success('Removed punches exported');
  };

  const exportVisitLog = () => {
    if (!reportData?.data?.length) return;
    const rows = reportData.data.map((r) => ({
      'Date': r.date,
      'Time': r.time,
      'Kind': r.kind,
      'Against': r.visit_type === 'other' ? 'Other' : (r.visit_type === 'outlet' ? 'Outlet' : 'Customer/Distributor'),
      'User': r.user_name,
      'Designation': r.designation,
      'Party': r.party,
      'Party Code': r.party_code,
      'Customer/Distributor': r.distributor_name,
      'City': r.city,
      'Route': r.route,
      'Zone': r.zone,
      'Purpose': r.purpose,
      'Contact Person': r.contact_person,
      'Contact No': r.contact_no,
      'Status': r.status,
      'Manual Entry': r.is_manual_entry ? 'Yes' : 'No',
      'Latitude': r.geo_location?.lat ?? '',
      'Longitude': r.geo_location?.lng ?? '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Visits');
    if (reportData.by_user?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        reportData.by_user.map((u) => ({
          'User': u.user, 'Designation': u.designation, 'Total': u.total,
          'New Visit': u['New Visit'], 'Re-Visit': u['Re-Visit'], 'Other Visit': u['Other Visit'],
        }))), 'By User');
    }
    if (reportData.by_purpose?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        reportData.by_purpose.map((x) => ({
          'Purpose': x.purpose, 'Total': x.total,
          'New Visit': x['New Visit'], 'Re-Visit': x['Re-Visit'], 'Other Visit': x['Other Visit'],
        }))), 'By Purpose');
    }
    XLSX.writeFile(wb, `Visit_Log_${fromDate}_to_${toDate}.xlsx`);
    toast.success('Visit log exported');
  };

  const exportTadaSheet = () => {
    if (!reportData?.data?.length) return;
    const rows = reportData.data.map((r) => ({
      'Employee Code': r.employee_code,
      'Name': r.user_name,
      'Designation': r.designation,
      'Grade': r.grade,
      'Headquarter': r.headquarter,
      'HQ Days': r.hq.days,
      'HQ Amount': r.hq.amount,
      'Ex-Stn With Bike Days': r.ex_with_bike.days,
      'Ex-Stn With Bike Amount': r.ex_with_bike.amount,
      'Ex-Stn No Bike Days': r.ex_no_bike.days,
      'Ex-Stn No Bike Amount': r.ex_no_bike.amount,
      'Out Station Days': r.out_station.days,
      'Out Station Fare': r.out_station_fare ?? 0,
      'Out Station Lodging (Co-Arrangement)': r.out_station_lodging_co ?? 0,
      'Out Station Lodging (Own Arrangement)': r.out_station_lodging_own ?? 0,
      'Out Station Incidentals': r.out_station_incidental ?? 0,
      'Out Station Amount': r.out_station.amount,
      'Other Expenses': r.other.amount,
      'Total KM': r.total_km,
      'Total Claimed': r.total_amount,
      'Pending Approval': r.pending_amount,
      'Entitlement HQ': r.entitlement.hq,
      'Entitlement Ex-Stn No Bike': r.entitlement.ex_no_bike,
      'Entitlement Ex-Stn With Bike': r.entitlement.ex_with_bike,
      'Entitlement O/S A': r.entitlement.os_a,
      'Entitlement O/S B': r.entitlement.os_b,
      'Entitlement O/S C': r.entitlement.os_c,
      'Entitlement Incidental': r.entitlement.incidental,
      'Travel Mode O/S': r.entitlement.travel_mode,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TADA Sheet');
    XLSX.writeFile(wb, `TADA_Sheet_${reportData.year}_${String(reportData.month).padStart(2, '0')}.xlsx`);
    toast.success('TADA sheet exported');
  };

  // Every report that lists records exposes them the same way, so one switch
  // serves them all.
  const rawRows = useMemo(() => {
    if (!reportData) return [];
    if (activeTab === 'visits') return reportData.data || [];
    return reportData.data || [];
  }, [reportData, activeTab]);

  const exportAllFields = () => {
    const flat = rawRows.map((r) => flattenRecord(r));
    if (!flat.length) return;
    const ws = XLSX.utils.json_to_sheet(flat, { header: allFieldColumns(flat) });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'All fields');
    XLSX.writeFile(wb, `${activeTab}_all_fields_${fromDate}_to_${toDate}.xlsx`);
    toast.success(`Exported ${flat.length} records with every field`);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      );
    }
    if (!reportData || reportType !== activeTab) {
      return (
        <div className="text-center py-12 text-slate-500">
          Select a date range and hit Refresh to load this report
        </div>
      );
    }
    if (showAllFields && isSuperadmin && rawRows.length > 0) {
      return <AllFieldsReport rows={rawRows} onExport={exportAllFields} label={activeTab} />;
    }
    switch (activeTab) {
      case 'overview':
        return <OverviewReport data={reportData} />;
      case 'visits':
        return <VisitsReport data={reportData} formatDateTime={formatDateTime} exportVisits={exportVisits} />;
      case 'customers':
        return <CustomersReport data={reportData} formatDateTime={formatDateTime}
                                scope={customerScope} setScope={setCustomerScope}
                                uncoveredOnly={uncoveredOnly} setUncoveredOnly={setUncoveredOnly}
                                exportCustomers={exportCustomers} />;
      case 'outlet-master':
        return <OutletMasterReport data={reportData} formatDateTime={formatDateTime}
                                   scope={outletScope} setScope={setOutletScope}
                                   uncoveredOnly={outletUncoveredOnly}
                                   setUncoveredOnly={setOutletUncoveredOnly}
                                   exportOutlets={exportOutletMaster} />;
      case 'expenses':
        return <ExpensesReport data={reportData} formatDateTime={formatDateTime} exportExpenses={exportExpenses} />;
      case 'activity':
        return <ActivityReport data={reportData} users={users}
                               kinds={activityKinds} setKinds={setActivityKinds}
                               pickedUsers={activityUsers} setPickedUsers={setActivityUsers}
                               onDetail={setActivityDetail} exportActivity={exportActivity} />;
      case 'new-visits':
        return <NewVisitsReport data={reportData} created={newVisitCreated}
                                setCreated={setNewVisitCreated} exportNew={exportNewVisits} />;
      case 'other-visits':
        return <OtherVisitsReport data={reportData} purpose={otherPurpose}
                                  setPurpose={setOtherPurpose} exportOther={exportOtherVisits} />;
      case 'visit-log':
        return <VisitLogReport data={reportData} visitKind={visitKind} setVisitKind={setVisitKind}
                               exportLog={exportVisitLog} />;
      case 'punch-removals':
        return <PunchRemovalsReport data={reportData} exportRemovals={exportPunchRemovals} />;
      case 'tada-sheet':
        return <TadaSheetReport data={reportData} exportSheet={exportTadaSheet}
                                downloadStatement={downloadStatement} />;
      default:
        return null;
    }
  };

  // Orders and Damages reports are withdrawn along with those modules (KEYNOTES 3, 4).
  const TABS = [
    { value: 'overview', label: 'Overview', Icon: LayoutDashboard },
    { value: 'activity', label: 'Activity', Icon: Activity },
    { value: 'visits', label: 'Visits', Icon: MapPin },
    { value: 'new-visits', label: 'New Visits', Icon: Award },
    { value: 'other-visits', label: 'Other Visits', Icon: Activity },
    { value: 'visit-log', label: 'Visit Log', Icon: ClipboardList },
    { value: 'expenses', label: 'TADA', Icon: Receipt },
    { value: 'tada-sheet', label: 'TADA Sheet', Icon: FileSpreadsheet },
    { value: 'customers', label: 'Customers', Icon: Truck },
    { value: 'outlet-master', label: 'Outlets', Icon: Store },
    // Only the super admin can remove punches, so only they see the record of it.
    ...(isSuperadmin ? [{ value: 'punch-removals', label: 'Removed Punches', Icon: ShieldCheck }] : []),
  ];

  return (
    <Layout>
      <div className="space-y-4 fade-in" data-testid="reports-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-sm text-slate-500">Trends, breakdowns and Excel exports</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          {activeTab === 'tada-sheet' ? (
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[120px]">
              <Label className="text-xs text-slate-500">Year</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="h-10 rounded-xl" data-testid="sheet-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEAR_CHOICES.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[150px]">
              <Label className="text-xs text-slate-500">Month</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger className="h-10 rounded-xl" data-testid="sheet-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((name, i) => (
                    <SelectItem key={name} value={String(i + 1)}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div className="relative flex-1 min-w-[220px]" ref={userPickerRef}>
                <Label className="text-xs text-slate-500">Users</Label>
                <button
                  type="button"
                  onClick={() => setUserPickerOpen((open) => !open)}
                  className="w-full h-10 rounded-xl border border-slate-200 px-3 text-left text-sm flex items-center justify-between"
                  data-testid="sheet-user-picker"
                >
                  <span className="truncate">
                    {sheetUsers.length === 0
                      ? 'All users'
                      : `${sheetUsers.length} selected`}
                  </span>
                  <ChevronDown className={cn('w-4 h-4 text-slate-400 shrink-0 transition-transform',
                                             userPickerOpen && 'rotate-180')} />
                </button>
                {userPickerOpen && (
                  <div className="absolute z-20 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
                    {/* Seventy-odd names is too many to scroll, so type to narrow it. */}
                    <div className="p-2 border-b border-slate-100">
                      <Input
                        autoFocus
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        placeholder="Search name, code or designation…"
                        className="h-9 rounded-lg text-sm"
                        data-testid="sheet-user-search"
                      />
                    </div>
                    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 text-xs">
                      <button
                        type="button"
                        className="text-sky-600 hover:underline"
                        onClick={() => setSheetUsers(visibleUsers.map((u) => u.id))}
                      >
                        Select all{userSearch ? ' shown' : ''}
                      </button>
                      <button
                        type="button"
                        className="text-slate-500 hover:underline"
                        onClick={() => setSheetUsers([])}
                      >
                        Clear (all users)
                      </button>
                    </div>
                    <div className="max-h-60 overflow-auto">
                      {visibleUsers.length === 0 ? (
                        <p className="px-3 py-6 text-center text-sm text-slate-400">
                          No one matches that
                        </p>
                      ) : visibleUsers.map((u) => {
                        const picked = sheetUsers.includes(u.id);
                        return (
                          <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={picked}
                              onChange={() => setSheetUsers((prev) => (
                                picked ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                              ))}
                            />
                            <span className="truncate">
                              {u.name}
                              {u.employee_code && (
                                <span className="ml-1 text-xs text-slate-400">{u.employee_code}</span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          ) : (
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs text-slate-500">From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-10 rounded-xl"
                data-testid="from-date-input"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs text-slate-500">To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-10 rounded-xl"
                data-testid="to-date-input"
              />
            </div>
            {isAdmin && (
              <div className="flex-1 min-w-[160px]">
                <Label className="text-xs text-slate-500">User</Label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger className="h-10 rounded-xl" data-testid="user-filter">
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={() => fetchReport(activeTab)}
              className="h-10 px-6 rounded-xl bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="refresh-report-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          )}
          {isSuperadmin && (
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-600 cursor-pointer"
                   data-testid="all-fields-toggle">
              <input
                type="checkbox"
                checked={showAllFields}
                onChange={(e) => setShowAllFields(e.target.checked)}
              />
              Show every field on this report
              <span className="text-xs text-slate-400">
                (super admin — the raw record, mandatory and optional alike)
              </span>
            </label>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto p-0">
            {TABS.map(({ value, label, Icon }) => (
              <TabsTrigger
                key={value}
                value={value}
                data-testid={`tab-${value}`}
                className={cn(
                  'px-4 py-2 rounded-xl text-sm font-medium transition-all',
                  'data-[state=active]:bg-sky-600 data-[state=active]:text-white',
                  'data-[state=inactive]:bg-white data-[state=inactive]:text-slate-600 data-[state=inactive]:border data-[state=inactive]:border-slate-200',
                )}
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="mt-4">{renderContent()}</div>
          <ActivityDetailDialog activity={activityDetail} onClose={() => setActivityDetail(null)} />
        </Tabs>
      </div>
    </Layout>
  );
};

// ====================== Reusable building blocks ======================

const KpiCard = ({ icon: Icon, label, value, accent = 'sky', sub, testId }) => {
  const accentMap = {
    sky: 'text-sky-600 bg-sky-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
    violet: 'text-violet-600 bg-violet-50',
    pink: 'text-pink-600 bg-pink-50',
    indigo: 'text-indigo-600 bg-indigo-50',
    red: 'text-red-600 bg-red-50',
    slate: 'text-slate-600 bg-slate-50',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', accentMap[accent])}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-xl font-bold text-slate-900 leading-tight">{value}</p>
          {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

const ChartCard = ({ title, subtitle, action, height = 280, children, testId }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testId}>
    <div className="flex items-start justify-between mb-3">
      <div>
        <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div style={{ width: '100%', height, minHeight: height }}>{children}</div>
  </div>
);

const EmptyChart = ({ label = 'No data in selected range' }) => (
  <div className="flex items-center justify-center h-full text-slate-400 text-sm">{label}</div>
);

// ====================== Overview Report ======================

const OverviewReport = ({ data }) => {
  const k = data?.kpis || {};
  const trend = data?.trend || [];
  // Map dates to short labels for charts
  const trendChart = trend.map(t => ({ ...t, label: shortDate(t.date) }));

  const hasTrend = trendChart.some(t => t.visits || t.expenses || t.km);

  return (
    <div className="space-y-4" data-testid="overview-content">
      {/* KPIs - order and damage tiles are withdrawn with those modules */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard icon={MapPin} label="Total Visits" value={k.visits_count || 0} accent="sky" sub={`${k.pending_visits || 0} pending`} testId="kpi-visits" />
        <KpiCard icon={Navigation} label="Total KM" value={`${(k.total_km || 0).toLocaleString('en-IN')} km`} accent="emerald" testId="kpi-km" />
        <KpiCard icon={Receipt} label="TADA & Expenses" value={formatINR(k.expenses_amount)} accent="amber" sub={`${formatINR(k.expenses_pending)} pending`} testId="kpi-expenses" />
        <KpiCard icon={Truck} label="New Distributors" value={k.distributors_count || 0} accent="violet" testId="kpi-distributors" />
        <KpiCard icon={Store} label="New Outlets" value={k.outlets_count || 0} accent="pink" testId="kpi-outlets" />
        <KpiCard icon={Activity} label="Pending Approvals" value={(k.pending_visits || 0) + (k.pending_expenses_count || 0)} accent="slate" testId="kpi-pending" />
      </div>

      {/* Visits + KM trend */}
      <ChartCard
        title="Daily Visits & KM Travelled"
        subtitle="Track field activity volume vs distance"
        height={280}
        testId="chart-visits-km"
      >
        {hasTrend ? (
          <ResponsiveContainer>
            <AreaChart data={trendChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="visitsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="kmGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.success} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={CHART_COLORS.success} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
              <YAxis yAxisId="left" stroke="#94a3b8" fontSize={11} />
              <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={11} />
              <Tooltip />
              <Legend />
              <Area yAxisId="left" type="monotone" dataKey="visits" name="Visits" stroke={CHART_COLORS.primary} fill="url(#visitsGrad)" />
              <Area yAxisId="right" type="monotone" dataKey="km" name="KM" stroke={CHART_COLORS.success} fill="url(#kmGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : <EmptyChart />}
      </ChartCard>

      {/* Expense trend */}
      <div className="grid grid-cols-1 gap-4">
        <ChartCard
          title="TADA & Expense Trend"
          subtitle="Daily TADA + Misc expenses claimed"
          height={260}
          testId="chart-expense-trend"
        >
          {hasTrend ? (
            <ResponsiveContainer>
              <BarChart data={trendChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Bar dataKey="expenses" name="Expense ₹" fill={CHART_COLORS.warning} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>
      </div>

    </div>
  );
};

// ====================== Visits Report ======================

const VisitsReport = ({ data, formatDateTime, exportVisits }) => {
  const summary = data?.daily_summary || [];
  const chartData = summary.map(d => ({
    label: shortDate(d.date),
    visits: d.total_visits,
    km: d.total_km,
    user: d.user_name,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={MapPin} label="Total Visits" value={data.count} accent="sky" testId="kpi-visits-total" />
        <KpiCard icon={Navigation} label="Total KM" value={`${data.total_km_all_days} km`} accent="emerald" testId="kpi-km-total" />
        <KpiCard icon={Calendar} label="Active Days" value={summary.length} accent="violet" testId="kpi-days" />
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <Button
            onClick={exportVisits}
            className="w-full h-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid="export-visits-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      <ChartCard title="Daily Visits vs KM" height={280} testId="visits-chart">
        {chartData.length ? (
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
              <YAxis yAxisId="left" stroke="#94a3b8" fontSize={11} />
              <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={11} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="visits" name="Visits" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="km" name="KM" fill={CHART_COLORS.success} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyChart />}
      </ChartCard>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-slate-50">
          <h3 className="font-semibold text-slate-900">Daily KM Summary</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">User</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Visits</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Total KM</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summary.map((day, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm text-slate-900 font-medium">{day.date}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{day.user_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 text-center">{day.total_visits}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-emerald-600 text-right">{day.total_km} km</td>
                </tr>
              ))}
              {summary.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">No visits in selected date range</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ====================== Orders Report ======================

const OrdersReport = ({ data, formatDateTime, exportOrders }) => {
  const orders = data?.data || [];

  // Daily aggregation
  const byDate = {};
  const byProduct = {};
  orders.forEach(o => {
    const d = (o.created_at || '').slice(0, 10);
    if (!byDate[d]) byDate[d] = { date: d, value: 0, count: 0 };
    byDate[d].value += o.total_value || 0;
    byDate[d].count += 1;
    (o.items || []).forEach(it => {
      const k = it.product_name || 'Unknown';
      if (!byProduct[k]) byProduct[k] = { name: k, qty: 0, value: 0 };
      byProduct[k].qty += it.quantity || 0;
      byProduct[k].value += it.line_total || 0;
    });
  });
  const trend = Object.values(byDate)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(t => ({ ...t, label: shortDate(t.date) }));
  const topProducts = Object.values(byProduct).sort((a, b) => b.value - a.value).slice(0, 6);

  // Distributor vs Outlet split
  const split = orders.reduce((acc, o) => {
    const k = o.order_type === 'Outlet' ? 'Outlet' : 'Distributor';
    acc[k] = (acc[k] || 0) + (o.total_value || 0);
    return acc;
  }, {});
  const splitData = Object.entries(split).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={ShoppingCart} label="Total Orders" value={data.count} accent="sky" testId="kpi-orders" />
        <KpiCard icon={TrendingUp} label="Total Value" value={formatINR(data.total_value)} accent="emerald" testId="kpi-orders-value" />
        <KpiCard icon={Award} label="Avg Order Value" value={data.count ? formatINR(Math.round((data.total_value || 0) / data.count)) : '₹0'} accent="indigo" testId="kpi-aov" />
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <Button
            onClick={exportOrders}
            className="w-full h-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid="export-orders-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ChartCard title="Daily Order Value" height={260} testId="orders-trend-chart">
            {trend.length ? (
              <ResponsiveContainer>
                <LineChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="value" name="Order ₹" stroke={CHART_COLORS.indigo} strokeWidth={2} />
                  <Line type="monotone" dataKey="count" name="Orders" stroke={CHART_COLORS.primary} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </ChartCard>
        </div>
        <ChartCard title="Outlet vs Distributor" subtitle="Order value split" height={260} testId="orders-split-chart">
          {splitData.length ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={splitData} dataKey="value" nameKey="name" outerRadius={80} label>
                  {splitData.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? CHART_COLORS.indigo : CHART_COLORS.pink} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>
      </div>

      <ChartCard title="Top Products by Order Value" height={280} testId="orders-top-products">
        {topProducts.length ? (
          <ResponsiveContainer>
            <BarChart data={topProducts} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={10} angle={-12} textAnchor="end" height={60} interval={0} />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip formatter={(v, n) => n === 'value' ? formatINR(v) : v} />
              <Legend />
              <Bar dataKey="value" name="₹ Value" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
              <Bar dataKey="qty" name="Quantity" fill={CHART_COLORS.success} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyChart />}
      </ChartCard>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Order #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Outlet/Dist</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Items</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map((order, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{order.order_number}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatDateTime(order.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      order.order_type === 'Outlet' ? 'bg-pink-100 text-pink-700' : 'bg-indigo-100 text-indigo-700',
                    )}>
                      {order.order_type || 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {order.outlet_name !== 'N/A' ? order.outlet_name : order.distributor_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 text-center">{order.items?.length || 0}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-emerald-600 text-right">{formatINR(order.total_value)}</td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">No orders in selected date range</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ====================== Expenses Report ======================

const ExpensesReport = ({ data, formatDateTime, exportExpenses }) => {
  const expenses = data?.data || [];
  const trend = (data?.daily_trend || []).map(t => ({ ...t, label: shortDate(t.date) }));
  const travel = data?.travel_type_breakdown || [];
  const modes = data?.mode_breakdown || [];
  const cats = data?.category_breakdown || [];
  const userBreak = data?.user_breakdown || [];

  // Status pie
  const statusData = [
    { name: 'Approved', value: data?.approved_amount || 0, color: CHART_COLORS.success },
    { name: 'Pending', value: data?.pending_amount || 0, color: CHART_COLORS.warning },
    { name: 'Rejected', value: data?.rejected_amount || 0, color: CHART_COLORS.danger },
  ].filter(s => s.value > 0);

  // TADA vs Other
  const typeData = [
    { name: 'TADA', value: data?.tada_amount || 0 },
    { name: 'Misc', value: data?.other_amount || 0 },
  ].filter(t => t.value > 0);

  return (
    <div className="space-y-4" data-testid="expenses-content">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <KpiCard icon={Receipt} label="Total Claims" value={data.count} accent="sky" testId="kpi-exp-count" />
        <KpiCard icon={TrendingUp} label="Total ₹" value={formatINR(data.total_amount)} accent="amber" testId="kpi-exp-total" />
        <KpiCard icon={CheckCircle2} label="Approved" value={formatINR(data.approved_amount)} accent="emerald" testId="kpi-exp-approved" />
        <KpiCard icon={Clock} label="Pending" value={formatINR(data.pending_amount)} accent="amber" testId="kpi-exp-pending" />
        <KpiCard icon={XCircle} label="Rejected" value={formatINR(data.rejected_amount)} accent="red" testId="kpi-exp-rejected" />
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <Button
            onClick={exportExpenses}
            className="w-full h-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid="export-expenses-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Travel highlights */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Navigation} label="TADA Spend" value={formatINR(data.tada_amount)} accent="violet" sub={`${data.tada_count || 0} TADA claims`} testId="kpi-tada" />
        <KpiCard icon={Bike} label="Distance Travelled" value={`${data.total_distance_km || 0} km`} accent="emerald" testId="kpi-tada-km" />
        <KpiCard icon={Package} label="Misc Spend" value={formatINR(data.other_amount)} accent="indigo" sub={`${data.other_count || 0} misc claims`} testId="kpi-misc" />
        <KpiCard icon={TrendingUp} label="Avg / Claim" value={data.count ? formatINR(Math.round((data.total_amount || 0) / data.count)) : '₹0'} accent="sky" testId="kpi-avg-claim" />
      </div>

      {/* Trend + breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ChartCard title="Daily Expense Trend" subtitle="TADA vs Misc breakdown" height={280} testId="expense-trend-chart">
            {trend.length ? (
              <ResponsiveContainer>
                <BarChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend />
                  <Bar dataKey="tada" name="TADA" stackId="a" fill={CHART_COLORS.violet} />
                  <Bar dataKey="other" name="Misc" stackId="a" fill={CHART_COLORS.warning} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </ChartCard>
        </div>

        <ChartCard title="Status Distribution" height={280} testId="expense-status-chart">
          {statusData.length ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={80} label={(e) => e.name}>
                  {statusData.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>
      </div>

      {/* Travel breakdown row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Travel Type Breakdown" subtitle="HQ vs Ex Station vs Out Station" height={260} testId="expense-travel-type-chart">
          {travel.length ? (
            <ResponsiveContainer>
              <BarChart data={travel} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="type" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip formatter={(v, n) => (n === 'amount' ? formatINR(v) : v)} />
                <Legend />
                <Bar dataKey="amount" name="Amount ₹" fill={CHART_COLORS.violet} radius={[4, 4, 0, 0]} />
                <Bar dataKey="km" name="KM" fill={CHART_COLORS.success} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart label="No TADA travel data in range" />}
        </ChartCard>

        <ChartCard title="Mode of Travel" height={260} testId="expense-mode-chart">
          {modes.length ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={modes} dataKey="amount" nameKey="mode" outerRadius={80} label={(e) => e.mode}>
                  {modes.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart label="No travel mode data" />}
        </ChartCard>

        <ChartCard title="Misc Expense Categories" subtitle="Courier / Internet / Printing / Misc" height={260} testId="expense-category-chart">
          {cats.length ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={cats} dataKey="amount" nameKey="category" outerRadius={80} label={(e) => e.category}>
                  {cats.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart label="No misc-expense data" />}
        </ChartCard>
      </div>

      {/* TADA vs Misc + per-user */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="TADA vs Misc" height={240} testId="expense-type-chart">
          {typeData.length ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={typeData} dataKey="value" nameKey="name" outerRadius={80} label={(e) => e.name}>
                  {typeData.map((_, i) => <Cell key={i} fill={i === 0 ? CHART_COLORS.violet : CHART_COLORS.warning} />)}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>

        <ChartCard title="Top Users by Expense" height={240} testId="expense-user-chart">
          {userBreak.length ? (
            <ResponsiveContainer>
              <BarChart data={userBreak.slice(0, 6)} layout="vertical" margin={{ top: 5, right: 10, left: 5, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                <YAxis type="category" dataKey="user" stroke="#94a3b8" fontSize={11} width={120} />
                <Tooltip formatter={(v, n) => n === 'amount' ? formatINR(v) : v} />
                <Legend />
                <Bar dataKey="amount" name="₹ Spent" fill={CHART_COLORS.amber || CHART_COLORS.warning} radius={[0, 4, 4, 0]} />
                <Bar dataKey="km" name="KM" fill={CHART_COLORS.success} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>
      </div>

      {/* Detail table — comprehensive */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-slate-50">
          <h3 className="font-semibold text-slate-900">Expense Details</h3>
          <p className="text-xs text-slate-500">Full breakdown with travel details, mode, distance and approval status</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">#</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">User</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Travel / Category</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">From → To</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Mode</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase">KM</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Amount</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expenses.map((e, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-900">{e.expense_number}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{e.date}</td>
                  <td className="px-3 py-2 text-slate-600">{e.created_by_name}</td>
                  <td className="px-3 py-2">
                    <span className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      e.expense_type === 'TADA' ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700',
                    )}>{e.expense_type}</span>
                    {e.is_auto_calculated && (
                      <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700">AUTO</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {e.travel_type || e.expense_category || '-'}
                  </td>
                  <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">
                    {e.from_location ? `${e.from_location} → ${e.to_location || ''}` : '-'}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{e.mode_of_travel || '-'}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{e.distance_km ? `${e.distance_km}` : '-'}</td>
                  <td className="px-3 py-2 text-right font-semibold text-amber-600">{formatINR(e.amount)}</td>
                  <td className="px-3 py-2">
                    <span className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      e.status === 'approved' && 'bg-emerald-100 text-emerald-700',
                      e.status === 'pending' && 'bg-amber-100 text-amber-700',
                      e.status === 'rejected' && 'bg-red-100 text-red-700',
                    )}>{e.status}</span>
                  </td>
                </tr>
              ))}
              {expenses.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-500">No expenses in selected date range</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ====================== Distributors / Outlets ======================

const DistributorsReport = ({ data, formatDateTime, exportDistributors }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <KpiCard icon={Truck} label="Total Distributors" value={data.count} accent="indigo" testId="kpi-dist-count" />
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <Button
            onClick={exportDistributors}
            className="w-full h-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid="export-distributors-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Route</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Zone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.data?.map((dist, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{dist.code}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{dist.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{dist.route_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{dist.zone_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{dist.primary_contact?.phone || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatDateTime(dist.created_at)}</td>
                </tr>
              ))}
              {(!data.data || data.data.length === 0) && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">No distributors in selected date range</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const OutletsReport = ({ data, formatDateTime, exportOutlets }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <KpiCard icon={Store} label="Total Outlets" value={data.count} accent="pink" testId="kpi-outlets-count" />
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <Button
            onClick={exportOutlets}
            className="w-full h-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid="export-outlets-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Route</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Zone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">City</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Distributor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.data?.map((outlet, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{outlet.outlet_id}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{outlet.outlet_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{outlet.outlet_type_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{outlet.route_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{outlet.zone_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{outlet.city_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{outlet.distributor_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{formatDateTime(outlet.created_at)}</td>
                </tr>
              ))}
              {(!data.data || data.data.length === 0) && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">No outlets in selected date range</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ====================== Damages ======================

const DamagesReport = ({ data, formatDateTime, exportDamages }) => {
  const damages = data?.data || [];

  // Aggregate by SKU
  const bySku = {};
  damages.forEach(d => {
    (d.entries || []).forEach(e => {
      const k = e.sku_name || 'Unknown';
      if (!bySku[k]) bySku[k] = { name: k, qty: 0 };
      bySku[k].qty += e.quantity || 0;
    });
  });
  const topSku = Object.values(bySku).sort((a, b) => b.qty - a.qty).slice(0, 6);

  // Aggregate by nature
  const byNature = {};
  damages.forEach(d => {
    (d.entries || []).forEach(e => {
      const k = e.nature_name || 'Unknown';
      if (!byNature[k]) byNature[k] = { name: k, value: 0 };
      byNature[k].value += e.quantity || 0;
    });
  });
  const natureData = Object.values(byNature).sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard icon={AlertTriangle} label="Damage Reports" value={data.count} accent="red" testId="kpi-damages-count" />
        <KpiCard icon={FileText} label="Items Damaged" value={data.total_items_damaged} accent="amber" testId="kpi-damages-items" />
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <Button
            onClick={exportDamages}
            className="w-full h-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid="export-damages-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top Damaged SKUs (Quantity)" height={260} testId="damages-sku-chart">
          {topSku.length ? (
            <ResponsiveContainer>
              <BarChart data={topSku} layout="vertical" margin={{ top: 5, right: 10, left: 5, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={120} />
                <Tooltip />
                <Bar dataKey="qty" name="Qty" fill={CHART_COLORS.danger} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>
        <ChartCard title="Damage Reasons" height={260} testId="damages-nature-chart">
          {natureData.length ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={natureData} dataKey="value" nameKey="name" outerRadius={80} label={(e) => e.name}>
                  {natureData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Damage #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Distributor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Outlet</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Entries</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {damages.map((damage, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{damage.damage_number}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{damage.complaint_date}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{damage.distributor_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{damage.outlet_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 text-center">{damage.entries?.length || 0}</td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      damage.status === 'resolved' && 'bg-emerald-100 text-emerald-700',
                      damage.status === 'pending' && 'bg-amber-100 text-amber-700',
                    )}>{damage.status}</span>
                  </td>
                </tr>
              ))}
              {damages.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">No damage reports in selected date range</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SheetCell = ({ days, amount }) => (
  <td className="px-3 py-2 text-right whitespace-nowrap">
    <span className="font-medium text-slate-900">{formatINR(amount)}</span>
    {days > 0 && <span className="ml-1 text-xs text-slate-400">({days}d)</span>}
  </td>
);

/**
 * The reimbursement card of 01.01.2022, filled in with what was actually claimed.
 *
 * The card prices Ex-Station differently with and without a bike, so the claims
 * are split the same way. Out Station is one column rather than the card's
 * A/B/C: those are city bands, and nothing on an expense record says which city
 * a trip was to - that needs a city-grade master before it can be split.
 */
const TadaSheetReport = ({ data, exportSheet, downloadStatement }) => {
  const rows = data?.data || [];
  const totals = data?.totals || { users: 0, claims: 0, amount: 0, pending: 0, km: 0 };
  const withClaims = rows.filter((r) => r.total_amount > 0);
  const unmapped = rows.filter((r) => !r.card_row && r.designation);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Users} label="Users" value={totals.users} accent="sky" testId="sheet-kpi-users" />
        <KpiCard icon={Receipt} label="Claims" value={totals.claims} accent="violet" testId="sheet-kpi-claims" />
        <KpiCard icon={TrendingUp} label="Total Claimed" value={formatINR(totals.amount)} accent="emerald" testId="sheet-kpi-amount" />
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <Button
            onClick={exportSheet}
            disabled={!rows.length}
            className="w-full h-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid="export-tada-sheet-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Export Sheet
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">
            TADA Sheet &mdash; {MONTH_NAMES[(data?.month || 1) - 1]} {data?.year}
          </h3>
          <p className="text-xs text-slate-500">
            Reimbursement structure effective {data.card_effective_from}. Amounts shown are what
            was claimed; the entitlement per day is in the card below. <b>Statement</b> exports
            that employee's month in the form Accounts receives &mdash; one row per day, signed
            off by the reporting manager and the GM/HOD.
          </p>
        </div>

        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">No users match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Employee</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Designation</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Grade</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">HQ</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Ex-Stn<br /><span className="text-[10px] font-normal">with bike</span></th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Ex-Stn<br /><span className="text-[10px] font-normal">no bike</span></th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Out Station</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Other</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Total KM</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Total</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Pending</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Statement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.user_id} className={cn(r.total_amount === 0 && 'text-slate-400')}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-medium text-slate-900">{r.user_name}</div>
                      <div className="text-xs text-slate-400">{r.employee_code} &middot; {r.headquarter}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.designation || '\u2014'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.grade || <span className="text-amber-600">not on card</span>}
                    </td>
                    <SheetCell {...r.hq} />
                    <SheetCell {...r.ex_with_bike} />
                    <SheetCell {...r.ex_no_bike} />
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <span className="font-medium text-slate-900">{formatINR(r.out_station.amount)}</span>
                      {r.out_station.days > 0 && (
                        <span className="ml-1 text-xs text-slate-400">({r.out_station.days}d)</span>
                      )}
                      {r.out_station.amount > 0 && (
                        <div className="text-xs text-slate-400">
                          fare {formatINR(r.out_station_fare)} · lodging{' '}
                          {formatINR((r.out_station_lodging_co || 0) + (r.out_station_lodging_own || 0))}
                          {r.out_station_incidental ? ` · inc ${formatINR(r.out_station_incidental)}` : ''}
                        </div>
                      )}
                    </td>
                    <SheetCell {...r.other} />
                    <td className="px-3 py-2 text-right whitespace-nowrap">{r.total_km}</td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900 whitespace-nowrap">{formatINR(r.total_amount)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {r.pending_amount > 0
                        ? <span className="text-amber-600">{formatINR(r.pending_amount)}</span>
                        : '\u2014'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-center">
                      <button
                        type="button"
                        onClick={() => downloadStatement(r.user_id, r.user_name, 'xlsx')}
                        className="px-2 py-1 mr-1 rounded-lg border border-slate-200 text-xs hover:bg-slate-50"
                        data-testid={`statement-xlsx-${r.user_id}`}
                      >
                        Excel
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadStatement(r.user_id, r.user_name, 'pdf')}
                        className="px-2 py-1 rounded-lg border border-slate-200 text-xs hover:bg-slate-50"
                        data-testid={`statement-pdf-${r.user_id}`}
                      >
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 font-semibold text-slate-900">
                <tr>
                  <td className="px-3 py-2" colSpan={8}>
                    {withClaims.length} of {rows.length} users claimed this month
                  </td>
                  <td className="px-3 py-2 text-right">{totals.km}</td>
                  <td className="px-3 py-2 text-right">{formatINR(totals.amount)}</td>
                  <td className="px-3 py-2 text-right">{formatINR(totals.pending)}</td>
                  <td className="px-3 py-2" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {unmapped.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <p className="font-medium">
            {unmapped.length} user(s) have a designation the reimbursement card does not name,
            so no entitlement can be shown for them:
          </p>
          <p className="mt-1">{[...new Set(unmapped.map((u) => u.designation))].join(', ')}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">Reimbursement structure &mdash; entitlement per day</h3>
          <p className="text-xs text-slate-500">
            Dairy Division [Milk &amp; Icecream], effective {data.card_effective_from}. Reference only
            &mdash; live TADA is calculated from Masters &gt; TADA Rates.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left whitespace-nowrap">Designation</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Grade</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">HQ &le;30km</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">HQ travel, no bike</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Ex-Stn no bike</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Ex-Stn with bike</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">O/S C</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">O/S B</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">O/S A</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Incidental</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Travel mode O/S</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Object.entries(data.card || {}).map(([name, c]) => (
                <tr key={name}>
                  <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{c.grade}</td>
                  <td className="px-3 py-2 text-right">{formatINR(c.hq)}</td>
                  <td className="px-3 py-2 text-slate-500">{c.hq_no_bike}</td>
                  <td className="px-3 py-2 text-right">{formatINR(c.ex_no_bike)}</td>
                  <td className="px-3 py-2 text-right">{formatINR(c.ex_with_bike)}</td>
                  <td className="px-3 py-2 text-right">{formatINR(c.os_c)}</td>
                  <td className="px-3 py-2 text-right">{formatINR(c.os_b)}</td>
                  <td className="px-3 py-2 text-right">{formatINR(c.os_a)}</td>
                  <td className="px-3 py-2 text-right">{formatINR(c.incidental)}</td>
                  <td className="px-3 py-2 text-slate-500">{c.travel_mode || '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
          Own arrangement is reimbursed at 50% of eligibility for every grade; incidental
          expenses apply only out-station. Authorised by HR.
        </p>
      </div>
    </div>
  );
};

const KIND_STYLES = {
  'New Visit': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Re-Visit': 'bg-sky-50 text-sky-700 border-sky-200',
  'Other Visit': 'bg-amber-50 text-amber-700 border-amber-200',
};

const KindBadge = ({ kind }) => (
  <span className={cn('px-2 py-0.5 rounded-full border text-xs whitespace-nowrap',
                      KIND_STYLES[kind] || 'bg-slate-50 text-slate-600 border-slate-200')}>
    {kind}
  </span>
);

/**
 * Every visit, told apart by kind.
 *
 * A New Visit is the call that first brought a customer or outlet onto the books,
 * a Re-Visit is any call on one already there, and an Other Visit had no party at
 * the other end at all. The three are what the field team is measured on, so they
 * are counted separately rather than lumped into one "visits" number.
 */
const PunchRemovalsReport = ({ data, exportRemovals }) => {
  const rows = data?.data || [];
  const summary = data?.summary || {
    total: 0, punch_in_removed: 0, punch_out_removed: 0, punch_out_corrected: 0, claims_withdrawn: 0,
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={ShieldCheck} label="Total changes" value={summary.total}
                 sub={`${summary.claims_withdrawn} TADA claims withdrawn`} accent="violet"
                 testId="kpi-punch-total" />
        <KpiCard icon={XCircle} label="Punch in removed" value={summary.punch_in_removed}
                 accent="red" testId="kpi-punch-in-removed" />
        <KpiCard icon={Clock} label="Punch out removed" value={summary.punch_out_removed}
                 sub="user punched again" accent="amber" testId="kpi-punch-out-removed" />
        <KpiCard icon={CheckCircle2} label="Punch out corrected" value={summary.punch_out_corrected}
                 sub="past days" accent="sky" testId="kpi-punch-out-corrected" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
          <h3 className="font-semibold text-slate-900">Removed &amp; corrected punches</h3>
          <Button onClick={exportRemovals} disabled={!rows.length}
                  className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="export-punch-removals-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">
            No punches were removed for days in this date range.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[32rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Day</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">User</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Action</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Old in / out</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">New out</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">TADA withdrawn</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Reason</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Done by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-900">{r.date}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.user_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium',
                        r.kind === 'tada_removed' ? 'bg-violet-100 text-violet-700'
                          : r.kind === 'tada_restored' ? 'bg-emerald-100 text-emerald-700'
                          : r.kind === 'in' ? 'bg-red-100 text-red-700'
                          : r.new_punch_out_time ? 'bg-sky-100 text-sky-700'
                            : 'bg-amber-100 text-amber-700')}>
                        {r.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      <div>In: {r.old_punch_in_time || '—'}</div>
                      <div>Out: {r.old_punch_out_time || '—'}{r.old_punch_out_auto ? ' (auto)' : ''}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      {r.new_punch_out_time ? (
                        <>
                          <div>{r.new_punch_out_time}</div>
                          <div className="text-slate-400">
                            {r.new_punch_out_source === 'last_visit' ? 'at last visit' : 'at punch in point'}
                          </div>
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.removed_tada_claim || (r.restored_tada_claim ? `${r.restored_tada_claim} (raised again)` : '—')}
                    </td>
                    <td className="px-3 py-2 min-w-[12rem] text-slate-600">{r.reason || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      <div className="text-slate-900">{r.removed_by_name}</div>
                      <div className="text-slate-400">{r.removed_on} {r.removed_time}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const VisitLogReport = ({ data, visitKind, setVisitKind, exportLog }) => {
  const rows = data?.data || [];
  const summary = data?.summary || { new: 0, revisit: 0, other: 0, total: 0, pending: 0, manual: 0, parties: 0 };
  const kinds = ['all', ...(data?.kinds || [])];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Award} label="New Visits" value={summary.new} accent="emerald" testId="kpi-new-visits" />
        <KpiCard icon={MapPin} label="Re-Visits" value={summary.revisit} accent="sky" testId="kpi-revisits" />
        <KpiCard icon={Activity} label="Other Visits" value={summary.other} accent="amber" testId="kpi-other-visits" />
        <KpiCard icon={ClipboardList} label="Total" value={summary.total}
                 sub={`${summary.parties} parties covered`} accent="violet" testId="kpi-total-visits" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-2">
        {kinds.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setVisitKind(k)}
            data-testid={`visit-kind-${k}`}
            className={cn('px-3 py-1.5 rounded-xl text-sm border transition-colors',
              visitKind === k
                ? 'bg-sky-600 text-white border-sky-600'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')}
          >
            {k === 'all' ? 'All kinds' : k}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          {summary.pending > 0 && <span className="text-amber-600">{summary.pending} awaiting approval</span>}
          {summary.manual > 0 && <span>{summary.manual} entered manually</span>}
          <Button onClick={exportLog} disabled={!rows.length}
                  className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="export-visit-log-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <h3 className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900">By purpose</h3>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Purpose</th>
                  <th className="px-3 py-2 text-right">New</th>
                  <th className="px-3 py-2 text-right">Re-Visit</th>
                  <th className="px-3 py-2 text-right">Other</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data?.by_purpose || []).map((x) => (
                  <tr key={x.purpose}>
                    <td className="px-3 py-2">{x.purpose}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{x['New Visit']}</td>
                    <td className="px-3 py-2 text-right text-sky-700">{x['Re-Visit']}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{x['Other Visit']}</td>
                    <td className="px-3 py-2 text-right font-semibold">{x.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <h3 className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900">By user</h3>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-right">New</th>
                  <th className="px-3 py-2 text-right">Re-Visit</th>
                  <th className="px-3 py-2 text-right">Other</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data?.by_user || []).map((u) => (
                  <tr key={u.user}>
                    <td className="px-3 py-2">
                      <div className="text-slate-900">{u.user}</div>
                      <div className="text-xs text-slate-400">{u.designation}</div>
                    </td>
                    <td className="px-3 py-2 text-right text-emerald-700">{u['New Visit']}</td>
                    <td className="px-3 py-2 text-right text-sky-700">{u['Re-Visit']}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{u['Other Visit']}</td>
                    <td className="px-3 py-2 text-right font-semibold">{u.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">
            {visitKind === 'all' ? 'All visits' : visitKind}
          </h3>
          <span className="text-xs text-slate-500">
            {rows.length} of {data?.total_in_window ?? rows.length} in this date range
          </span>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">No visits match this filter.</div>
        ) : (
          <div className="overflow-x-auto max-h-[32rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Date &amp; time</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Kind</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">User</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Party</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">City / Route</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Purpose</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Contact</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-slate-900">{r.date}</div>
                      <div className="text-xs text-slate-400">{r.time}</div>
                    </td>
                    <td className="px-3 py-2"><KindBadge kind={r.kind} /></td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.user_name}</td>
                    <td className="px-3 py-2">
                      <div className="text-slate-900">{r.party || '\u2014'}</div>
                      {r.party_code && <div className="text-xs text-slate-400">{r.party_code}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{r.city || '\u2014'}</div>
                      <div className="text-xs text-slate-400">{r.route}</div>
                    </td>
                    <td className="px-3 py-2">{r.purpose}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{r.contact_person || '\u2014'}</div>
                      <div className="text-xs text-slate-400">{r.contact_no}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.status === 'approved'
                        ? <span className="text-emerald-600">approved</span>
                        : <span className="text-amber-600">{r.status}</span>}
                      {r.is_manual_entry && (
                        <div className="text-xs text-slate-400">manual</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Every field of every record, for the super admin.
 *
 * The other reports are curated - they show the handful of fields that report is
 * about. This one shows the record as it is stored, so nothing optional is
 * invisible just because no report was designed around it.
 */
const AllFieldsReport = ({ rows, onExport, label }) => {
  const flat = useMemo(() => rows.map((r) => flattenRecord(r)), [rows]);
  const columns = useMemo(() => allFieldColumns(flat), [flat]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <div>
          <h3 className="font-semibold text-slate-900">All fields — {label}</h3>
          <p className="text-xs text-slate-500">
            {flat.length} records, {columns.length} fields each. Nested values are
            flattened into dotted columns; lists are shown as JSON.
          </p>
        </div>
        <Button onClick={onExport} disabled={!flat.length}
                className="ml-auto h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="export-all-fields-btn">
          <Download className="w-4 h-4 mr-2" />
          Export all fields
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-auto max-h-[36rem]">
          <table className="text-xs">
            <thead className="bg-slate-50 text-slate-500 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left font-semibold sticky left-0 bg-slate-50 z-10">#</th>
                {columns.map((c) => (
                  <th key={c} className="px-2 py-2 text-left font-semibold whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {flat.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-2 py-1.5 text-slate-400 sticky left-0 bg-white">{i + 1}</td>
                  {columns.map((c) => (
                    <td key={c} className="px-2 py-1.5 whitespace-nowrap max-w-[22rem] truncate"
                        title={String(row[c] ?? '')}>
                      {row[c] === '' || row[c] === undefined
                        ? <span className="text-slate-300">—</span>
                        : String(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * The customer/distributor master, with what has actually happened against each one.
 *
 * The old Distributors tab listed whoever was added in the date range. That can
 * answer "who is new" but never "who has nobody called on", which is the question
 * a sales manager actually has - so this defaults to the whole master and counts
 * each customer's visits and outlets inside the window.
 */
const CustomersReport = ({ data, formatDateTime, scope, setScope,
                           uncoveredOnly, setUncoveredOnly, exportCustomers }) => {
  const all = data?.data || [];
  const summary = data?.summary || {};
  const rows = uncoveredOnly ? all.filter((c) => c.visits_in_period === 0) : all;

  const Breakdown = ({ title, items, label }) => (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <h3 className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900">{title}</h3>
      <div className="overflow-x-auto max-h-64">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left">{label}</th>
              <th className="px-3 py-2 text-right">Customers</th>
              <th className="px-3 py-2 text-right">Outlets</th>
              <th className="px-3 py-2 text-right">Visits</th>
              <th className="px-3 py-2 text-right">Not visited</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(items || []).map((x) => (
              <tr key={x.name}>
                <td className="px-3 py-2">{x.name}</td>
                <td className="px-3 py-2 text-right">{x.customers}</td>
                <td className="px-3 py-2 text-right">{x.outlets}</td>
                <td className="px-3 py-2 text-right">{x.visits}</td>
                <td className={cn('px-3 py-2 text-right', x.uncovered > 0 && 'text-amber-600')}>
                  {x.uncovered}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Truck} label="Customers" value={summary.customers ?? 0}
                 sub={`${summary.added_in_period ?? 0} added in period`} accent="indigo" testId="kpi-cust-count" />
        <KpiCard icon={Store} label="Outlets attached" value={summary.outlets_total ?? 0} accent="pink" testId="kpi-cust-outlets" />
        <KpiCard icon={MapPin} label="Visits in period" value={summary.visits_in_period ?? 0} accent="sky" testId="kpi-cust-visits" />
        <KpiCard icon={AlertTriangle} label="Never visited" value={summary.uncovered ?? 0}
                 sub="in this date range" accent="amber" testId="kpi-cust-uncovered" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          {[['all', 'Whole master'], ['added', 'Added in period']].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setScope(value)}
              data-testid={`customer-scope-${value}`}
              className={cn('px-3 py-1.5 rounded-xl text-sm border transition-colors',
                scope === value
                  ? 'bg-sky-600 text-white border-sky-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={uncoveredOnly}
                 onChange={(e) => setUncoveredOnly(e.target.checked)}
                 data-testid="customer-uncovered-only" />
          Only those never visited
        </label>
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          {summary.no_contact > 0 && <span>{summary.no_contact} without a phone number</span>}
          {summary.without_location > 0 && <span className="text-amber-600">{summary.without_location} without a location</span>}
          <Button onClick={exportCustomers} disabled={!rows.length}
                  className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="export-customers-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Breakdown title="By type" items={data?.by_type} label="Type" />
        <Breakdown title="By zone" items={data?.by_zone} label="Zone" />
        <Breakdown title="By who added them" items={data?.by_adder} label="User" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">
            {uncoveredOnly ? 'Customers never visited in this range' : 'Customers'}
          </h3>
          <span className="text-xs text-slate-500">{rows.length} of {all.length}</span>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">No customers match this filter.</div>
        ) : (
          <div className="overflow-auto max-h-[34rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Code</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Name</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Type</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Route / Zone</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">City</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Contact</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Outlets</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Visits</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Last visit</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Added by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{c.code}</td>
                    <td className="px-3 py-2">{c.name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{c.distributor_type || '\u2014'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{c.route_name}</div>
                      <div className="text-xs text-slate-400">{c.zone_name}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {c.city_name || '\u2014'}
                      {c.city_is_inferred && (
                        <span className="ml-1 text-xs text-amber-500" title="Inferred from the nearest outlet, not confirmed">~</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{c.primary_contact_name || '\u2014'}</div>
                      <div className="text-xs text-slate-400">{c.primary_contact_phone}</div>
                    </td>
                    <td className="px-3 py-2 text-right">{c.outlets_total}</td>
                    <td className={cn('px-3 py-2 text-right',
                                      c.visits_in_period === 0 && 'text-amber-600 font-medium')}>
                      {c.visits_in_period}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {c.last_visit_at ? c.last_visit_at.slice(0, 10) : <span className="text-amber-600">never</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{c.added_by_display}</div>
                      <div className="text-xs text-slate-400">{formatDateTime(c.created_at).slice(0, 11)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * The outlet master, with what has happened against each one.
 *
 * Same shape as the customer report and for the same reason: a list of outlets
 * opened this month cannot answer "which outlets has nobody called on", which is
 * the question worth asking. Volumes are carried through because an outlet's
 * worth is what it moves, not that it exists.
 */
const OutletMasterReport = ({ data, formatDateTime, scope, setScope,
                              uncoveredOnly, setUncoveredOnly, exportOutlets }) => {
  const all = data?.data || [];
  const summary = data?.summary || {};
  const rows = uncoveredOnly ? all.filter((o) => o.visits_in_period === 0) : all;

  const Breakdown = ({ title, items, label, showVolume = true }) => (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <h3 className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900">{title}</h3>
      <div className="overflow-x-auto max-h-64">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left">{label}</th>
              <th className="px-3 py-2 text-right">Outlets</th>
              <th className="px-3 py-2 text-right">Visits</th>
              <th className="px-3 py-2 text-right">Not visited</th>
              {showVolume && <th className="px-3 py-2 text-right">Volume</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(items || []).map((x) => (
              <tr key={x.name}>
                <td className="px-3 py-2">{x.name}</td>
                <td className="px-3 py-2 text-right">{x.outlets}</td>
                <td className="px-3 py-2 text-right">{x.visits}</td>
                <td className={cn('px-3 py-2 text-right', x.uncovered > 0 && 'text-amber-600')}>{x.uncovered}</td>
                {showVolume && <td className="px-3 py-2 text-right">{x.volume?.toLocaleString('en-IN')}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Store} label="Outlets" value={summary.outlets ?? 0}
                 sub={`${summary.added_in_period ?? 0} added in period`} accent="pink" testId="kpi-out-count" />
        <KpiCard icon={MapPin} label="Visits in period" value={summary.visits_in_period ?? 0} accent="sky" testId="kpi-out-visits" />
        <KpiCard icon={AlertTriangle} label="Never visited" value={summary.uncovered ?? 0}
                 sub="in this date range" accent="amber" testId="kpi-out-uncovered" />
        <KpiCard icon={Package} label="Total volume" value={(summary.total_volume ?? 0).toLocaleString('en-IN')}
                 sub={`${summary.with_volumes ?? 0} outlets declared volumes`} accent="emerald" testId="kpi-out-volume" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          {[['all', 'Whole master'], ['added', 'Added in period']].map(([value, label]) => (
            <button key={value} type="button" onClick={() => setScope(value)}
                    data-testid={`outlet-scope-${value}`}
                    className={cn('px-3 py-1.5 rounded-xl text-sm border transition-colors',
                      scope === value ? 'bg-sky-600 text-white border-sky-600'
                                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')}>
              {label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={uncoveredOnly}
                 onChange={(e) => setUncoveredOnly(e.target.checked)}
                 data-testid="outlet-uncovered-only" />
          Only those never visited
        </label>
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          {summary.no_contact > 0 && <span>{summary.no_contact} without a phone number</span>}
          {summary.with_asset > 0 && <span>{summary.with_asset} carry an asset</span>}
          <Button onClick={exportOutlets} disabled={!rows.length}
                  className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="export-outlet-master-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {(data?.by_segment || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Declared volume by segment</h3>
          <div className="flex flex-wrap gap-3">
            {data.by_segment.map((x) => (
              <div key={x.segment} className="px-4 py-3 rounded-xl border border-slate-200 bg-slate-50">
                <div className="text-xs text-slate-500">{x.segment}</div>
                <div className="text-lg font-semibold text-slate-900">
                  {x.volume.toLocaleString('en-IN')} <span className="text-xs font-normal text-slate-500">{x.uom}</span>
                </div>
                <div className="text-xs text-slate-400">{x.outlets} outlets</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Breakdown title="By retailer type" items={data?.by_type} label="Type" />
        <Breakdown title="By zone" items={data?.by_zone} label="Zone" />
        <Breakdown title="By activation type" items={data?.by_activation} label="Activation" />
        <Breakdown title="By who added them" items={data?.by_adder} label="User" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">
            {uncoveredOnly ? 'Outlets never visited in this range' : 'Outlets'}
          </h3>
          <span className="text-xs text-slate-500">{rows.length} of {all.length}</span>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">No outlets match this filter.</div>
        ) : (
          <div className="overflow-auto max-h-[34rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">ID</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Name</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Type</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Customer</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Route / Zone</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">City</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Asset</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Volume</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Visits</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Last visit</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Added by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{o.outlet_id}</td>
                    <td className="px-3 py-2">{o.outlet_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{o.retailer_type_name}</div>
                      {o.activation_type && <div className="text-xs text-slate-400">{o.activation_type}</div>}
                    </td>
                    <td className="px-3 py-2">{o.distributor_name || '\u2014'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{o.route_name}</div>
                      <div className="text-xs text-slate-400">{o.zone_name}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{o.city_name || '\u2014'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{o.asset_name || '\u2014'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {o.segment_volume_total > 0 ? o.segment_volume_total.toLocaleString('en-IN') : '\u2014'}
                      {o.segments_count > 0 && (
                        <div className="text-xs text-slate-400">{o.segments_count} segment(s)</div>
                      )}
                    </td>
                    <td className={cn('px-3 py-2 text-right',
                                      o.visits_in_period === 0 && 'text-amber-600 font-medium')}>
                      {o.visits_in_period}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{o.last_contact_date || '—'}</div>
                      <div className={cn('text-xs',
                        o.last_contact_source === 'Visit' ? 'text-slate-400' : 'text-amber-600')}>
                        {o.last_contact_source === 'Visit' ? o.last_contact_time : 'added, not visited'}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{o.added_by_display}</div>
                      <div className="text-xs text-slate-400">{formatDateTime(o.created_at).slice(0, 11)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * First visits - the calls that brought something new onto the books.
 *
 * A first visit is not just a visit: it carries a customer or an outlet into
 * existence with it. So each row is paired with the record it created and with
 * where that record's approval stands, because a new visit whose customer was
 * rejected developed nothing, and counting it would flatter the number.
 */
const NewVisitsReport = ({ data, created, setCreated, exportNew }) => {
  const rows = data?.data || [];
  const summary = data?.summary || {};

  const Breakdown = ({ title, items, label }) => (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <h3 className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900">{title}</h3>
      <div className="overflow-x-auto max-h-72">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left">{label}</th>
              <th className="px-3 py-2 text-right">New</th>
              <th className="px-3 py-2 text-right">Customers</th>
              <th className="px-3 py-2 text-right">Outlets</th>
              <th className="px-3 py-2 text-right">Volume</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(items || []).map((x) => (
              <tr key={x.name}>
                <td className="px-3 py-2">{x.name}</td>
                <td className="px-3 py-2 text-right font-semibold">{x.total}</td>
                <td className="px-3 py-2 text-right text-indigo-700">{x.customers}</td>
                <td className="px-3 py-2 text-right text-pink-700">{x.outlets}</td>
                <td className="px-3 py-2 text-right">{x.volume ? x.volume.toLocaleString('en-IN') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Award} label="New visits" value={summary.new_visits ?? 0}
                 sub={`${summary.users ?? 0} users developing`} accent="emerald" testId="kpi-nv-total" />
        <KpiCard icon={Truck} label="Customers added" value={summary.customers_added ?? 0} accent="indigo" testId="kpi-nv-customers" />
        <KpiCard icon={Store} label="Outlets added" value={summary.outlets_added ?? 0} accent="pink" testId="kpi-nv-outlets" />
        <KpiCard icon={Package} label="Declared volume" value={(summary.declared_volume ?? 0).toLocaleString('en-IN')}
                 sub="from new outlets" accent="amber" testId="kpi-nv-volume" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          {[['all', 'Everything new'], ['customer', 'Customers only'], ['outlet', 'Outlets only']]
            .map(([value, label]) => (
              <button key={value} type="button" onClick={() => setCreated(value)}
                      data-testid={`new-visit-created-${value}`}
                      className={cn('px-3 py-1.5 rounded-xl text-sm border transition-colors',
                        created === value ? 'bg-sky-600 text-white border-sky-600'
                                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')}>
                {label}
              </button>
            ))}
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          {summary.pending_approval > 0 && (
            <span className="text-amber-600">{summary.pending_approval} awaiting approval</span>
          )}
          {summary.rejected > 0 && <span className="text-rose-600">{summary.rejected} rejected</span>}
          {summary.orphan > 0 && <span className="text-rose-600">{summary.orphan} with nothing linked</span>}
          <Button onClick={exportNew} disabled={!rows.length}
                  className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="export-new-visits-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Breakdown title="By user" items={data?.by_user} label="User" />
        <Breakdown title="By zone" items={data?.by_zone} label="Zone" />
        <Breakdown title="By purpose" items={data?.by_purpose} label="Purpose" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">What was brought onto the books</h3>
          <span className="text-xs text-slate-500">{rows.length} first visits</span>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">No new visits in this range.</div>
        ) : (
          <div className="overflow-auto max-h-[34rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Date &amp; time</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">User</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Created</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Code</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Name</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Type</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">City / Zone</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Volume</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Purpose</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Approval</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-slate-900">{r.date}</div>
                      <div className="text-xs text-slate-400">{r.time}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.user_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={cn('px-2 py-0.5 rounded-full border text-xs',
                        r.created_kind === 'customer' ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                        : r.created_kind === 'outlet' ? 'bg-pink-50 text-pink-700 border-pink-200'
                        : 'bg-rose-50 text-rose-700 border-rose-200')}>
                        {r.created_kind === 'customer' ? 'Customer'
                         : r.created_kind === 'outlet' ? 'Outlet' : 'Nothing linked'}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{r.created_code || '\u2014'}</td>
                    <td className="px-3 py-2">
                      <div>{r.created_name || '\u2014'}</div>
                      {r.parent_customer && (
                        <div className="text-xs text-slate-400">under {r.parent_customer}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.created_type || '\u2014'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{r.city || '\u2014'}</div>
                      <div className="text-xs text-slate-400">{r.zone}</div>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {r.declared_volume > 0 ? r.declared_volume.toLocaleString('en-IN') : '\u2014'}
                    </td>
                    <td className="px-3 py-2">{r.purpose}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.approval_status === 'approved' ? <span className="text-emerald-600">approved</span>
                       : r.approval_status === 'rejected' ? <span className="text-rose-600">rejected</span>
                       : r.approval_status === 'pending' ? <span className="text-amber-600">pending</span>
                       : '\u2014'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Calls with no customer or outlet at the other end.
 *
 * A plant visit, a meeting at Head Office, a lineman who was not where he should
 * have been. They still cost the day's travel and still need accounting for, but
 * no party name can describe them - so each is placed by the nearest outlet on
 * file, and the contact the user recorded is the only other evidence of who was
 * actually seen.
 */
const OtherVisitsReport = ({ data, purpose, setPurpose, exportOther }) => {
  const rows = data?.data || [];
  const summary = data?.summary || {};
  const purposes = ['all', ...(data?.purposes_available || [])];
  const peakHour = (data?.by_hour || []).reduce((a, b) => (b.total > (a?.total ?? 0) ? b : a), null);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Activity} label="Other visits" value={summary.total ?? 0}
                 sub={`${summary.users ?? 0} users`} accent="amber" testId="kpi-ov-total" />
        <KpiCard icon={ClipboardList} label="Distinct purposes" value={summary.purposes ?? 0} accent="violet" testId="kpi-ov-purposes" />
        <KpiCard icon={Users} label="Contact captured" value={summary.with_contact ?? 0}
                 sub={summary.without_contact ? `${summary.without_contact} missing` : 'all of them'}
                 accent="emerald" testId="kpi-ov-contact" />
        <KpiCard icon={Clock} label="Busiest hour"
                 value={peakHour ? `${String(peakHour.hour).padStart(2, '0')}:00` : '—'}
                 sub={peakHour ? `${peakHour.total} visits, IST` : ''} accent="sky" testId="kpi-ov-hour" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-2">
        {purposes.map((x) => (
          <button key={x} type="button" onClick={() => setPurpose(x)}
                  data-testid={`other-purpose-${x}`}
                  className={cn('px-3 py-1.5 rounded-xl text-sm border transition-colors',
                    purpose === x ? 'bg-sky-600 text-white border-sky-600'
                                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')}>
            {x === 'all' ? 'All purposes' : x}
          </button>
        ))}
        <Button onClick={exportOther} disabled={!rows.length}
                className="ml-auto h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="export-other-visits-btn">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <h3 className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900">By purpose</h3>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Purpose</th>
                  <th className="px-3 py-2 text-right">Visits</th>
                  <th className="px-3 py-2 text-right">Users</th>
                  <th className="px-3 py-2 text-right">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data?.by_purpose || []).map((x) => (
                  <tr key={x.name}>
                    <td className="px-3 py-2">{x.name}</td>
                    <td className="px-3 py-2 text-right font-semibold">{x.total}</td>
                    <td className="px-3 py-2 text-right">{x.users}</td>
                    <td className="px-3 py-2 text-right text-slate-500">
                      {summary.total ? `${Math.round((x.total / summary.total) * 100)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <h3 className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900">By user</h3>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-right">Visits</th>
                  <th className="px-3 py-2 text-right">Purposes used</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data?.by_user || []).map((x) => (
                  <tr key={x.name}>
                    <td className="px-3 py-2">{x.name}</td>
                    <td className="px-3 py-2 text-right font-semibold">{x.total}</td>
                    <td className="px-3 py-2 text-right">{x.purposes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">
            {purpose === 'all' ? 'All other visits' : purpose}
          </h3>
          <span className="text-xs text-slate-500">{rows.length} visits</span>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">No other visits in this range.</div>
        ) : (
          <div className="overflow-auto max-h-[34rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Date &amp; time</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">User</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Purpose</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Met</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Where</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-slate-900">{r.date}</div>
                      <div className="text-xs text-slate-400">{r.time}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{r.user_name}</div>
                      <div className="text-xs text-slate-400">{r.designation}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 rounded-full border text-xs bg-amber-50 text-amber-700 border-amber-200 whitespace-nowrap">
                        {r.purpose}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{r.contact_person || '\u2014'}</div>
                      <div className="text-xs text-slate-400">{r.contact_no}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.nearest_place
                        ? (<>
                            <div>near {r.nearest_place}</div>
                            <div className="text-xs text-slate-400">
                              {r.nearest_place_km} km away
                              {r.nearest_place_km > 3 && (
                                <span className="ml-1 text-amber-500" title="No outlet nearby, so the place is only a rough guide">~</span>
                              )}
                            </div>
                          </>)
                        : '\u2014'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.status === 'approved'
                        ? <span className="text-emerald-600">approved</span>
                        : <span className="text-amber-600">{r.status}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * A dropdown that lets you type to narrow a long list and tick several at once.
 *
 * Built here rather than reused from the Users picker because that one is wired
 * to its own state; this takes its options as a prop so the activity report can
 * drive two of them - one for processes, one for people.
 */
const MultiSearchSelect = ({ label, options, value, onChange, allLabel, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  }, [options, search]);

  return (
    <div className="relative flex-1 min-w-[200px]" ref={boxRef}>
      <Label className="text-xs text-slate-500">{label}</Label>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testId}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-left text-sm flex items-center justify-between">
        <span className="truncate">
          {value.length === 0 ? allLabel : `${value.length} selected`}
        </span>
        <ChevronDown className={cn('w-4 h-4 text-slate-400 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="p-2 border-b border-slate-100">
            <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Type to search…" className="h-9 rounded-lg text-sm"
                   data-testid={`${testId}-search`} />
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 text-xs">
            <button type="button" className="text-sky-600 hover:underline"
                    onClick={() => onChange(shown.map((o) => o.value))}>
              Select all{search ? ' shown' : ''}
            </button>
            <button type="button" className="text-slate-500 hover:underline" onClick={() => onChange([])}>
              {allLabel}
            </button>
          </div>
          <div className="max-h-60 overflow-auto">
            {shown.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-400">Nothing matches that</p>
            ) : shown.map((o) => {
              const picked = value.includes(o.value);
              return (
                <label key={o.value} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={picked}
                         onChange={() => onChange(picked ? value.filter((v) => v !== o.value)
                                                         : [...value, o.value])} />
                  <span className="truncate">{o.label}</span>
                  {o.hint && <span className="ml-auto text-xs text-slate-400">{o.hint}</span>}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const ACTIVITY_STYLES = {
  'New Visit': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Re-Visit': 'bg-sky-50 text-sky-700 border-sky-200',
  'Other Visit': 'bg-amber-50 text-amber-700 border-amber-200',
  'Customer/Distributor Added': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Outlet Added': 'bg-pink-50 text-pink-700 border-pink-200',
  'Order Booked': 'bg-violet-50 text-violet-700 border-violet-200',
  'Damage Reported': 'bg-rose-50 text-rose-700 border-rose-200',
  'TADA Claim': 'bg-teal-50 text-teal-700 border-teal-200',
  'Punch In': 'bg-slate-100 text-slate-700 border-slate-200',
  'Punch Out': 'bg-slate-100 text-slate-700 border-slate-200',
};

/** Everything that happened, from every process, on one timeline. */
const ActivityReport = ({ data, users, kinds, setKinds, pickedUsers, setPickedUsers,
                          onDetail, exportActivity }) => {
  const rows = data?.data || [];
  const summary = data?.summary || {};
  const kindOptions = (data?.activities_available || []).map((a) => ({ value: a, label: a }));
  const userOptions = (users || []).map((u) => ({
    value: u.id, label: u.name, hint: u.employee_code,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Activity} label="Activities" value={summary.total ?? 0} accent="violet" testId="kpi-act-total" />
        <KpiCard icon={Users} label="Users active" value={summary.users ?? 0} accent="sky" testId="kpi-act-users" />
        <KpiCard icon={Navigation} label="With location" value={summary.with_location ?? 0} accent="emerald" testId="kpi-act-located" />
        <KpiCard icon={Calendar} label="Days covered" value={summary.days ?? 0} accent="amber" testId="kpi-act-days" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <MultiSearchSelect label="Activity" options={kindOptions} value={kinds}
                             onChange={setKinds} allLabel="All activities" testId="activity-kind-picker" />
          <MultiSearchSelect label="Activity by" options={userOptions} value={pickedUsers}
                             onChange={setPickedUsers} allLabel="All users" testId="activity-user-picker" />
          <Button onClick={exportActivity} disabled={!rows.length}
                  className="h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="export-activity-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
        {(data?.by_activity || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {data.by_activity.map((x) => (
              <span key={x.name}
                    className={cn('px-2 py-1 rounded-lg border text-xs', ACTIVITY_STYLES[x.name] || 'bg-slate-50 border-slate-200')}>
                {x.name} · {x.total}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Activity</h3>
          <span className="text-xs text-slate-500">{rows.length} records, newest first</span>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">Nothing happened in this range.</div>
        ) : (
          <div className="overflow-auto max-h-[36rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Activity Date</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Activity</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Activity By</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Distributor</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Outlet</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">GMAP URL</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={`${r.activity}-${i}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-slate-900">{r.activity_date_ist}</div>
                      <div className="text-xs text-slate-400">{r.activity_time_ist}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={cn('px-2 py-0.5 rounded-full border text-xs whitespace-nowrap',
                                          ACTIVITY_STYLES[r.activity] || 'bg-slate-50 border-slate-200')}>
                        {r.activity}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.activity_by || '\u2014'}</td>
                    <td className="px-3 py-2">{r.distributor || '\u2014'}</td>
                    <td className="px-3 py-2">{r.outlet || '\u2014'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.gmap_url
                        ? <a href={r.gmap_url} target="_blank" rel="noreferrer"
                             className="text-sky-600 hover:underline inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Open map
                          </a>
                        : <span className="text-slate-300">\u2014</span>}
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <button type="button" onClick={() => onDetail(r)}
                              className="px-2 py-1 rounded-lg border border-slate-200 text-xs hover:bg-slate-50"
                              data-testid={`activity-view-${i}`}>
                        View details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/** The record behind one activity row, exactly as it is stored. */
const ActivityDetailDialog = ({ activity, onClose }) => {
  if (!activity) return null;
  const details = flattenRecord(activity.details || {});
  const keys = Object.keys(details);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
         onClick={onClose} data-testid="activity-detail-dialog">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900">{activity.activity}</h3>
            <p className="text-sm text-slate-500">
              {activity.activity_by} · {(activity.activity_date || '').replace('T', ' ').slice(0, 19)}
            </p>
            {(activity.distributor || activity.outlet) && (
              <p className="text-sm text-slate-500">
                {[activity.distributor, activity.outlet].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose}
                  className="text-slate-400 hover:text-slate-600" data-testid="activity-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-auto px-5 py-4">
          {activity.gmap_url && (
            <a href={activity.gmap_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 mb-3 text-sm text-sky-600 hover:underline">
              <MapPin className="w-4 h-4" /> Open this location in Google Maps
            </a>
          )}
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {keys.map((k) => (
                <tr key={k}>
                  <td className="py-2 pr-4 text-slate-500 align-top whitespace-nowrap w-1/3">{k}</td>
                  <td className="py-2 text-slate-900 break-all">
                    {details[k] === '' ? <span className="text-slate-300">—</span> : String(details[k])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-slate-100 text-right">
          <Button onClick={onClose} className="h-9 rounded-xl bg-slate-900 hover:bg-slate-800 text-white">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Reports;
